import { normalizeGitRemote } from "./types/mailbox.js";
/**
 * Canonical key for a github.com repository, used to match git remotes
 * identically across the API, web and CLI (ENG-277).
 *
 * Normalizes via {@link normalizeGitRemote} and returns lowercase `owner/name`
 * (no `.git`). Returns `null` for empty input, non-github.com hosts (GitLab,
 * Bitbucket, GitHub Enterprise) or when owner/name is missing.
 *
 * @example
 * githubRepoKey("git@github.com:Owner/Repo.git") // → "owner/repo"
 * githubRepoKey("https://gitlab.com/owner/repo") // → null
 */
export function githubRepoKey(remote) {
    if (typeof remote !== "string")
        return null;
    const normalized = normalizeGitRemote(remote);
    const match = /^https:\/\/([^/]+)\/(.*)$/.exec(normalized);
    if (!match)
        return null;
    const host = match[1];
    const path = match[2];
    if (host !== "github.com" || path === undefined)
        return null;
    const segments = path.split("/");
    if (segments.length !== 2)
        return null;
    const [owner, name] = segments;
    if (!owner || !name)
        return null;
    return `${owner}/${name}`.toLowerCase();
}
/**
 * Case-insensitive comparison key for ANY git remote (ENG-353).
 *
 * GitHub (and practically every git host) treats owner/repo names
 * case-insensitively, but {@link normalizeGitRemote} preserves path case, so
 * `https://github.com/PaisleyAdmin/CompBox` and
 * `https://github.com/paisleyadmin/compbox` would otherwise not match. This
 * key normalizes the transport form AND lowercases the whole result.
 *
 * Use it only for COMPARISON — stored and displayed remotes keep their
 * original case. Returns `null` for empty / missing input so an absent remote
 * never "matches" another absent remote by accident.
 *
 * @example
 * canonicalRemoteKey("git@github.com:Owner/Repo.git") // → "https://github.com/owner/repo"
 * canonicalRemoteKey("  ")                            // → null
 */
export function canonicalRemoteKey(remote) {
    if (typeof remote !== "string")
        return null;
    const normalized = normalizeGitRemote(remote);
    if (normalized === "")
        return null;
    return normalized.toLowerCase();
}
/**
 * True when two git remotes point at the same repository, ignoring transport
 * form (scp-like / ssh:// / https, `.git`, trailing slash, port) and letter
 * case (ENG-353). Returns `false` when either side is empty or missing.
 */
export function sameGitRemote(a, b) {
    const keyA = canonicalRemoteKey(a);
    if (keyA === null)
        return false;
    return keyA === canonicalRemoteKey(b);
}
//# sourceMappingURL=github-repo.js.map